import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../../../lib/supabase-auth';
import { PortalLayout } from '../../../../components/portal/PortalLayout';
import { useAuth } from '../../../../contexts/AuthContextLazy';
import ClientSelector from '../../../../components/compliance/ClientSelector';
import WizardSteps from '../../../../components/compliance/WizardSteps';
import VersionComparison from '../../../../components/compliance/VersionComparison';
import { showWarning, showInfo, showSuccess, showError } from '../../../../utils/toast';

interface NeedsAnalysisData {
  personal_info: {
    full_name: string;
    date_of_birth: string;
    occupation: string;
    employer: string;
  };
  family: {
    marital_status: string;
    spouse_name?: string;
    dependents: Array<{
      name: string;
      age: number;
      relationship: string;
    }>;
  };
  financial: {
    annual_income: number;
    spouse_income?: number;
    total_assets: number;
    total_liabilities: number;
    monthly_expenses: number;
  };
  insurance: {
    life_policies: Array<{
      carrier: string;
      policy_type: string;
      coverage: number;
      annual_premium: number;
    }>;
    disability_coverage?: number;
    critical_illness?: number;
  };
  goals: {
    retirement_age: number;
    education_funding_needed: boolean;
    estate_planning_concerns: string;
  };
}

const WIZARD_STEPS = [
  { id: 'client', name: 'Client Selection' },
  { id: 'personal', name: 'Personal Information' },
  { id: 'family', name: 'Family & Dependents' },
  { id: 'financial', name: 'Financial Information' },
  { id: 'insurance', name: 'Insurance Coverage' },
  { id: 'goals', name: 'Goals & Objectives' },
  { id: 'summary', name: 'Review & Sign' }
];

export default function NeedsAnalysisWizard() {
  const navigate = useNavigate();
  const { documentId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const preselectedClientId = searchParams.get('clientId');
  const [documentData, setDocumentData] = useState<NeedsAnalysisData>({
    personal_info: {
      full_name: '',
      date_of_birth: '',
      occupation: '',
      employer: ''
    },
    family: {
      marital_status: 'single',
      dependents: []
    },
    financial: {
      annual_income: 0,
      total_assets: 0,
      total_liabilities: 0,
      monthly_expenses: 0
    },
    insurance: {
      life_policies: []
    },
    goals: {
      retirement_age: 65,
      education_funding_needed: false,
      estate_planning_concerns: ''
    }
  });
  const [previousVersion, setPreviousVersion] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [documentRecord, setDocumentRecord] = useState<any>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [dataChanged, setDataChanged] = useState(false);

  useEffect(() => {
    if (documentId) {
      loadExistingDocument();
    } else if (preselectedClientId) {
      // Load the pre-selected client
      loadPreselectedClient();
    }
  }, [documentId, preselectedClientId]);

  // Load saved data from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem('needs_analysis_draft');
    if (savedData && !documentId) {
      try {
        const parsed = JSON.parse(savedData);
        if (parsed.clientId === selectedClient?.id) {
          setDocumentData(parsed.data);
          showInfo('Restored unsaved changes');
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Error loading saved data:', error);
        }
      }
    }
  }, [selectedClient?.id]);

  // Auto-save functionality
  useEffect(() => {
    if (!autoSaveEnabled || !dataChanged || !selectedClient) return;

    const saveTimer = setTimeout(async () => {
      // Save to localStorage immediately
      localStorage.setItem('needs_analysis_draft', JSON.stringify({
        clientId: selectedClient.id,
        data: documentData,
        timestamp: new Date().toISOString()
      }));

      // Save to database
      await saveProgress();
      setDataChanged(false);
      setLastSaved(new Date());
    }, 30000); // 30 seconds

    return () => clearTimeout(saveTimer);
  }, [documentData, dataChanged, autoSaveEnabled, selectedClient]);

  const loadPreselectedClient = async () => {
    if (!preselectedClientId) return;
    
    try {
      const { data: client, error } = await supabase
        .from('parties')
        .select('id, name, primary_email')
        .eq('id', preselectedClientId)
        .single();
        
      if (error) throw error;
      
      if (client) {
        setSelectedClient(client);
        // Also update personal info with client name
        updateField('personal_info.full_name', client.name);
      }
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error loading pre-selected client:', error);
        }

      }
    }
  };

  useEffect(() => {
    if (selectedClient && currentStep === 0) {
      // Load previous needs analysis if exists
      loadPreviousNeedsAnalysis();
    }
  }, [selectedClient]);

  const loadExistingDocument = async () => {
    try {
      setLoading(true);
      const { data: doc, error } = await supabase
        .from('compliance_documents')
        .select(`
          *,
          client:parties(id, name, primary_email),
          current_version:document_versions!compliance_documents_current_version_id_fkey(
            id,
            content,
            version_number
          )
        `)
        .eq('id', documentId)
        .single();

      if (error) throw error;

      if (doc) {
        setDocumentRecord(doc);
        setSelectedClient(doc.client);
        if (doc.current_version?.content) {
          setDocumentData(doc.current_version.content);
        }
      }
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error loading document:', error);
        }

      }
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Failed to load document');
        }

      }
    } finally {
      setLoading(false);
    }
  };

  const loadPreviousNeedsAnalysis = async () => {
    if (!selectedClient?.id) return;

    try {
      // Get the most recent signed needs analysis for this client
      const { data, error } = await supabase
        .from('compliance_documents')
        .select(`
          *,
          current_version:document_versions!compliance_documents_current_version_id_fkey(
            id,
            content,
            version_number,
            created_at
          )
        `)
        .eq('client_id', selectedClient.id)
        .eq('document_type', 'needs_analysis')
        .eq('status', 'signed')
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;

      if (data && data.length > 0 && data[0].current_version) {
        setPreviousVersion(data[0].current_version);
        // Pre-fill with previous data as starting point
        setDocumentData(data[0].current_version.content);
      }
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error loading previous needs analysis:', error);
        }

      }
    }
  };

  const updateField = (path: string, value: any) => {
    const keys = path.split('.');
    setDocumentData((prev) => {
      const newData = { ...prev };
      let current: any = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!(keys[i] in current)) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
    setDataChanged(true);
  };

  const saveProgress = async () => {
    if (!selectedClient) return;

    try {
      setSaving(true);

      if (documentRecord) {
        // Update existing document
        const { data: version, error: versionError } = await supabase
          .rpc('create_document_version', {
            p_document_id: documentRecord.id,
            p_content: documentData,
            p_created_by: user?.id,
            p_change_summary: 'Progress saved'
          });

        if (versionError) throw versionError;
      } else {
        // Create new document
        const { data: doc, error: docError } = await supabase
          .from('compliance_documents')
          .insert({
            client_id: selectedClient.id,
            advisor_id: user?.id,
            document_type: 'needs_analysis',
            status: 'draft'
          })
          .select()
          .single();

        if (docError) throw docError;

        // Create first version
        const { data: version, error: versionError } = await supabase
          .from('document_versions')
          .insert({
            document_id: doc.id,
            version_number: 1,
            is_current: true,
            content: documentData,
            created_by: user?.id
          })
          .select()
          .single();

        if (versionError) throw versionError;

        // Update document with current version
        await supabase
          .from('compliance_documents')
          .update({ current_version_id: version.id })
          .eq('id', doc.id);

        setDocumentRecord(doc);
      }

      // console.log('Progress saved');
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error saving progress:', error);
        }

      }
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Failed to save progress');
        }

      }
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (currentStep === 0 && !selectedClient) {
      showWarning('Please select a client');
      return;
    }

    // Save progress before moving to next step
    await saveProgress();

    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    if (stepIndex < currentStep) {
      setCurrentStep(stepIndex);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Client Selection
        return (
          <ClientSelector
            onSelectClient={setSelectedClient}
            documentType="needs_analysis"
          />
        );

      case 1: // Personal Information
        return (
          <div className="bg-white portal-content-shadow rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Personal Information</h2>
            {previousVersion ? (
              <VersionComparison
                previousVersion={previousVersion}
                currentVersion={documentData}
                fields={[
                  { path: 'personal_info.full_name', label: 'Full Name', type: 'text' },
                  { path: 'personal_info.date_of_birth', label: 'Date of Birth', type: 'date' },
                  { path: 'personal_info.occupation', label: 'Occupation', type: 'text' },
                  { path: 'personal_info.employer', label: 'Employer', type: 'text' }
                ]}
                onUpdateField={updateField}
                sectionTitle="Personal Information"
              />
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={documentData.personal_info.full_name}
                    onChange={(e) => updateField('personal_info.full_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    value={documentData.personal_info.date_of_birth}
                    onChange={(e) => updateField('personal_info.date_of_birth', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Occupation
                  </label>
                  <input
                    type="text"
                    value={documentData.personal_info.occupation}
                    onChange={(e) => updateField('personal_info.occupation', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employer
                  </label>
                  <input
                    type="text"
                    value={documentData.personal_info.employer}
                    onChange={(e) => updateField('personal_info.employer', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 bg-white"
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 2: // Family & Dependents
        return (
          <div className="bg-white portal-content-shadow rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Family & Dependents</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Marital Status
                </label>
                <select
                  value={documentData.family.marital_status}
                  onChange={(e) => updateField('family.marital_status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 bg-white"
                >
                  <option value="single">Single</option>
                  <option value="married">Married</option>
                  <option value="common_law">Common Law</option>
                  <option value="divorced">Divorced</option>
                  <option value="widowed">Widowed</option>
                </select>
              </div>

              {(documentData.family.marital_status === 'married' || 
                documentData.family.marital_status === 'common_law') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Spouse Name
                  </label>
                  <input
                    type="text"
                    value={documentData.family.spouse_name || ''}
                    onChange={(e) => updateField('family.spouse_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 bg-white"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dependents
                </label>
                <div className="space-y-2">
                  {documentData.family.dependents.map((dep, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Name"
                        value={dep.name}
                        onChange={(e) => {
                          const newDeps = [...documentData.family.dependents];
                          newDeps[index].name = e.target.value;
                          updateField('family.dependents', newDeps);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white"
                      />
                      <input
                        type="number"
                        placeholder="Age"
                        value={dep.age}
                        onChange={(e) => {
                          const newDeps = [...documentData.family.dependents];
                          newDeps[index].age = parseInt(e.target.value);
                          updateField('family.dependents', newDeps);
                        }}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newDeps = documentData.family.dependents.filter((_, i) => i !== index);
                          updateField('family.dependents', newDeps);
                        }}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const newDeps = [...documentData.family.dependents, { name: '', age: 0, relationship: 'child' }];
                      updateField('family.dependents', newDeps);
                    }}
                    className="text-amber-600 hover:text-amber-700 font-medium"
                  >
                    + Add Dependent
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 3: // Financial Information
        return (
          <div className="bg-white portal-content-shadow rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Financial Information</h2>
            {previousVersion ? (
              <VersionComparison
                previousVersion={previousVersion}
                currentVersion={documentData}
                fields={[
                  { path: 'financial.annual_income', label: 'Annual Income', type: 'currency' },
                  { path: 'financial.spouse_income', label: 'Spouse Income', type: 'currency' },
                  { path: 'financial.total_assets', label: 'Total Assets', type: 'currency' },
                  { path: 'financial.total_liabilities', label: 'Total Liabilities', type: 'currency' },
                  { path: 'financial.monthly_expenses', label: 'Monthly Expenses', type: 'currency' }
                ]}
                onUpdateField={updateField}
                sectionTitle="Financial Information"
              />
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Annual Income
                    </label>
                    <input
                      type="number"
                      value={documentData.financial.annual_income}
                      onChange={(e) => updateField('financial.annual_income', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 bg-white"
                    />
                  </div>
                  {(documentData.family.marital_status === 'married' || 
                    documentData.family.marital_status === 'common_law') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Spouse Income
                      </label>
                      <input
                        type="number"
                        value={documentData.financial.spouse_income || 0}
                        onChange={(e) => updateField('financial.spouse_income', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 bg-white"
                      />
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Assets
                    </label>
                    <input
                      type="number"
                      value={documentData.financial.total_assets}
                      onChange={(e) => updateField('financial.total_assets', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Liabilities
                    </label>
                    <input
                      type="number"
                      value={documentData.financial.total_liabilities}
                      onChange={(e) => updateField('financial.total_liabilities', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 bg-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Monthly Expenses
                  </label>
                  <input
                    type="number"
                    value={documentData.financial.monthly_expenses}
                    onChange={(e) => updateField('financial.monthly_expenses', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 bg-white"
                  />
                </div>
                <div className="mt-4 space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-2">Financial Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Net Worth:</span>
                        <span className="font-semibold text-gray-900">
                          ${(documentData.financial.total_assets - documentData.financial.total_liabilities).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Household Income:</span>
                        <span className="text-gray-900">
                          ${(documentData.financial.annual_income + (documentData.financial.spouse_income || 0)).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Debt-to-Income Ratio:</span>
                        <span className="text-gray-900">
                          {documentData.financial.annual_income > 0 
                            ? `${Math.round((documentData.financial.total_liabilities / documentData.financial.annual_income) * 100)}%`
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Quick calculators */}
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <h4 className="font-medium text-amber-900 mb-2">Insurance Needs Calculator</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-amber-700">10x Income Rule:</span>
                        <span className="text-amber-900 font-medium">
                          ${(documentData.financial.annual_income * 10).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-amber-700">DIME Method Estimate:</span>
                        <span className="text-amber-900 font-medium">
                          ${(
                            documentData.financial.total_liabilities + 
                            (documentData.financial.annual_income * 10) + 
                            (documentData.financial.monthly_expenses * 12 * 2) + 
                            (documentData.family.dependents.length * 100000)
                          ).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-amber-600 mt-2">
                      DIME = Debt + Income replacement + Mortgage + Education costs
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 4: // Insurance Coverage
        return (
          <div className="bg-white portal-content-shadow rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Current Insurance Coverage</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Life Insurance Policies</h3>
                <div className="space-y-3">
                  {documentData.insurance.life_policies.map((policy, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input
                          type="text"
                          placeholder="Insurance Carrier"
                          value={policy.carrier}
                          onChange={(e) => {
                            const newPolicies = [...documentData.insurance.life_policies];
                            newPolicies[index].carrier = e.target.value;
                            updateField('insurance.life_policies', newPolicies);
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white"
                        />
                        <select
                          value={policy.policy_type}
                          onChange={(e) => {
                            const newPolicies = [...documentData.insurance.life_policies];
                            newPolicies[index].policy_type = e.target.value;
                            updateField('insurance.life_policies', newPolicies);
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white"
                        >
                          <option value="">Select Type</option>
                          <option value="term">Term Life</option>
                          <option value="whole">Whole Life</option>
                          <option value="universal">Universal Life</option>
                        </select>
                        <input
                          type="number"
                          placeholder="Coverage Amount"
                          value={policy.coverage}
                          onChange={(e) => {
                            const newPolicies = [...documentData.insurance.life_policies];
                            newPolicies[index].coverage = parseFloat(e.target.value) || 0;
                            updateField('insurance.life_policies', newPolicies);
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white"
                        />
                        <input
                          type="number"
                          placeholder="Annual Premium"
                          value={policy.annual_premium}
                          onChange={(e) => {
                            const newPolicies = [...documentData.insurance.life_policies];
                            newPolicies[index].annual_premium = parseFloat(e.target.value) || 0;
                            updateField('insurance.life_policies', newPolicies);
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newPolicies = documentData.insurance.life_policies.filter((_, i) => i !== index);
                          updateField('insurance.life_policies', newPolicies);
                        }}
                        className="mt-2 text-red-600 hover:text-red-700 text-sm"
                      >
                        Remove Policy
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const newPolicies = [...documentData.insurance.life_policies, {
                        carrier: '',
                        policy_type: '',
                        coverage: 0,
                        annual_premium: 0
                      }];
                      updateField('insurance.life_policies', newPolicies);
                    }}
                    className="text-amber-600 hover:text-amber-700 font-medium"
                  >
                    + Add Life Insurance Policy
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Disability Coverage
                  </label>
                  <input
                    type="number"
                    value={documentData.insurance.disability_coverage || 0}
                    onChange={(e) => updateField('insurance.disability_coverage', parseFloat(e.target.value) || 0)}
                    placeholder="Monthly benefit amount"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Critical Illness Coverage
                  </label>
                  <input
                    type="number"
                    value={documentData.insurance.critical_illness || 0}
                    onChange={(e) => updateField('insurance.critical_illness', parseFloat(e.target.value) || 0)}
                    placeholder="Coverage amount"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 bg-white"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 5: // Goals & Objectives
        return (
          <div className="bg-white portal-content-shadow rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Goals & Objectives</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Planned Retirement Age
                </label>
                <input
                  type="number"
                  value={documentData.goals.retirement_age}
                  onChange={(e) => updateField('goals.retirement_age', parseInt(e.target.value) || 65)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 bg-white"
                />
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={documentData.goals.education_funding_needed}
                    onChange={(e) => updateField('goals.education_funding_needed', e.target.checked)}
                    className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Education funding needed for dependents
                  </span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estate Planning Concerns
                </label>
                <textarea
                  value={documentData.goals.estate_planning_concerns}
                  onChange={(e) => updateField('goals.estate_planning_concerns', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 bg-white"
                  placeholder="Describe any estate planning concerns or objectives..."
                />
              </div>
            </div>
          </div>
        );

      case 6: // Summary
        return (
          <div className="bg-white portal-content-shadow rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Review Needs Analysis</h2>
            
            <div className="space-y-6">
              {/* Client Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Client Information</h3>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Name</dt>
                    <dd className="text-sm text-gray-900">{documentData.personal_info.full_name || selectedClient?.name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Date of Birth</dt>
                    <dd className="text-sm text-gray-900">{documentData.personal_info.date_of_birth || 'Not provided'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Occupation</dt>
                    <dd className="text-sm text-gray-900">{documentData.personal_info.occupation || 'Not provided'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Marital Status</dt>
                    <dd className="text-sm text-gray-900">{documentData.family.marital_status}</dd>
                  </div>
                </dl>
              </div>

              {/* Financial Summary */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Financial Summary</h3>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Annual Income</dt>
                    <dd className="text-sm text-gray-900">${documentData.financial.annual_income.toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Total Assets</dt>
                    <dd className="text-sm text-gray-900">${documentData.financial.total_assets.toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Total Liabilities</dt>
                    <dd className="text-sm text-gray-900">${documentData.financial.total_liabilities.toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Net Worth</dt>
                    <dd className="text-sm text-gray-900 font-medium">
                      ${(documentData.financial.total_assets - documentData.financial.total_liabilities).toLocaleString()}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Insurance Coverage */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Current Insurance Coverage</h3>
                {documentData.insurance.life_policies.length > 0 ? (
                  <ul className="space-y-2">
                    {documentData.insurance.life_policies.map((policy, index) => (
                      <li key={index} className="text-sm text-gray-900">
                        • {policy.carrier} - {policy.policy_type} - ${policy.coverage.toLocaleString()} coverage
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">No existing life insurance coverage</p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      I confirm this information is accurate
                    </span>
                  </label>
                  <button
                    onClick={() => {
                      // TODO: Implement signature flow
                      showInfo('Document ready for signature');
                    }}
                    className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg"
                  >
                    Send for Signature
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/portal/compliance')}
              className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Needs Analysis Wizard</h1>
              <p className="text-gray-600 mt-1">
                {selectedClient ? `For ${selectedClient.name}` : 'Select a client to begin'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center text-sm text-gray-600">
              <input
                type="checkbox"
                checked={autoSaveEnabled}
                onChange={(e) => setAutoSaveEnabled(e.target.checked)}
                className="mr-2 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              Auto-save
            </label>
            {saving && (
              <span className="text-sm text-gray-500 flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-500 mr-2"></div>
                Saving...
              </span>
            )}
            {lastSaved && !saving && (
              <span className="text-sm text-gray-500">
                Last saved: {lastSaved.toLocaleTimeString()} ✓
              </span>
            )}
          </div>
        </div>

        {/* Progress Steps */}
        <WizardSteps
          steps={WIZARD_STEPS}
          currentStep={currentStep}
          onStepClick={handleStepClick}
        />

        {/* Step Content */}
        <div className="mt-8">
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        <div className="mt-8 flex justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className={`flex items-center px-4 py-2 rounded-lg font-medium ${
              currentStep === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Previous
          </button>

          <button
            onClick={handleNext}
            className="flex items-center px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium"
          >
            {currentStep === WIZARD_STEPS.length - 1 ? 'Complete' : 'Next'}
            <ArrowRightIcon className="h-5 w-5 ml-2" />
          </button>
        </div>
      </div>
    </PortalLayout>
  );
}